/**
 * Step 3: customize the chosen template — toggle events + tasks, optionally
 * set event dates + guest counts inline with each enabled event.
 */
import React from 'react';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Checkbox } from '../ui/checkbox';
import DateInput from '../ui/date-input';

function slug(s) { return s.replace(/\s+/g, '-').toLowerCase(); }

function EventRow({ evt, budget, currency, isChecked, onToggle, dateValue, guestValue, onDateChange, onGuestChange }) {
  const evtBudget = budget > 0 ? (budget * evt.budget_pct / 100) : 0;
  return (
    <div
      className={`p-3 rounded-sm border transition-colors ${
        isChecked ? 'border-border bg-white' : 'border-border/50 bg-page/50 opacity-60'
      }`}
    >
      <div className="flex items-start gap-3">
        <Checkbox
          checked={isChecked}
          onCheckedChange={onToggle}
          className="mt-0.5"
          data-testid={`event-toggle-${slug(evt.name)}`}
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between">
            <span className="font-sans text-sm text-charcoal">{evt.name}</span>
            <span className="font-mono text-xs text-muted">
              {evt.budget_pct}%{evtBudget > 0 && ` ~ ${currency} ${evtBudget.toLocaleString('en-US', { maximumFractionDigits: 0 })}`}
            </span>
          </div>
          <p className="text-xs text-muted truncate">{evt.description}</p>
        </div>
      </div>
      {isChecked && (
        <div className="grid grid-cols-2 gap-2 mt-2 ml-7">
          <DateInput
            value={dateValue}
            onChange={(iso) => onDateChange(iso)}
            className="input-field h-8 text-xs font-mono"
            data-testid={`event-date-${slug(evt.name)}`}
          />
          <Input
            type="number"
            value={guestValue || ''}
            onChange={(e) => onGuestChange(parseInt(e.target.value, 10) || 0)}
            className="input-field h-8 text-xs font-mono"
            placeholder="Guest count"
            min="0"
            data-testid={`event-guests-${slug(evt.name)}`}
          />
        </div>
      )}
    </div>
  );
}

function TaskRow({ task, isChecked, onToggle }) {
  const priorityClass =
    task.priority === 'high' ? 'bg-terracotta/10 text-terracotta border-terracotta/30' :
    task.priority === 'medium' ? 'bg-ochre/10 text-ochre border-ochre/30' :
    'bg-moss/10 text-moss border-moss/30';
  return (
    <div
      className={`flex items-center gap-3 p-2.5 rounded-sm border transition-colors ${
        isChecked ? 'border-border bg-white' : 'border-border/50 bg-page/50 opacity-60'
      }`}
    >
      <Checkbox
        checked={isChecked}
        onCheckedChange={onToggle}
        data-testid={`task-toggle-${slug(task.title)}`}
      />
      <div className="flex-1 flex items-center justify-between min-w-0">
        <span className="text-sm text-charcoal truncate">{task.title}</span>
        <Badge className={`text-xs ml-2 flex-shrink-0 border ${priorityClass}`}>
          {task.priority}
        </Badge>
      </div>
    </div>
  );
}

export default function CustomizeTemplateStep({
  templateDetail,
  budget, currency,
  selectedEvents, onToggleEvent,
  selectedTasks, onToggleTask,
  eventDates, setEventDates,
  eventGuestCounts, setEventGuestCounts,
}) {
  if (!templateDetail) return null;
  return (
    <div className="space-y-6 mt-4">
      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-serif text-lg text-charcoal">Events</h3>
          <span className="text-xs text-muted">
            {selectedEvents.length}/{templateDetail.events.length} selected
          </span>
        </div>
        <div className="space-y-2">
          {templateDetail.events.map((evt) => (
            <EventRow
              key={evt.name}
              evt={evt}
              budget={budget}
              currency={currency}
              isChecked={selectedEvents.includes(evt.name)}
              onToggle={() => onToggleEvent(evt.name)}
              dateValue={eventDates[evt.name]}
              guestValue={eventGuestCounts[evt.name]}
              onDateChange={(v) => setEventDates((prev) => ({ ...prev, [evt.name]: v }))}
              onGuestChange={(v) => setEventGuestCounts((prev) => ({ ...prev, [evt.name]: v }))}
            />
          ))}
        </div>
      </div>
      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-serif text-lg text-charcoal">Checklist Tasks</h3>
          <span className="text-xs text-muted">
            {selectedTasks.length}/{templateDetail.checklist.length} selected
          </span>
        </div>
        <div className="space-y-1.5">
          {templateDetail.checklist.map((task) => (
            <TaskRow
              key={task.title}
              task={task}
              isChecked={selectedTasks.includes(task.title)}
              onToggle={() => onToggleTask(task.title)}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
